import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { LibraryService } from '../../services';
import { queryKeys } from '../keys';
import type { LibraryItem, LibraryKind, LibraryPage, LibraryProduct } from '../../domain';

const PAGE_SIZE = 50;

/**
 * One product's half of the shared SmartAI library, paged by `hasMore`.
 * The Lattice half is not retried: a failure there is shown as a notice beside
 * the Coral half, and retrying an unreachable product only delays that notice.
 */
export function useLibrary(
  product: LibraryProduct,
  options?: { kind?: LibraryKind; enabled?: boolean },
) {
  const kind = options?.kind;
  return useInfiniteQuery({
    queryKey: queryKeys.library(product, kind),
    queryFn: ({ pageParam }): Promise<LibraryPage> =>
      product === 'coral'
        ? LibraryService.listCoral({ kind, page: pageParam, pageSize: PAGE_SIZE })
        : LibraryService.listLattice({ kind, page: pageParam, pageSize: PAGE_SIZE }),
    initialPageParam: 1,
    getNextPageParam: last => (last.hasMore ? last.page + 1 : undefined),
    enabled: options?.enabled ?? true,
    retry: product === 'lattice' ? false : undefined,
  });
}

/**
 * Import a Lattice image or video into the caller's Coral assets. On success the
 * Coral asset lists, folders and library half are refreshed so it shows up.
 */
export function useImportFromLattice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (item: LibraryItem) => LibraryService.importFromLattice(item),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets'] });
      qc.invalidateQueries({ queryKey: ['library', 'coral'] });
    },
  });
}
